import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';

import { Box, Button, Flex, Input, Menu, Separator, Stack, Text, Textarea } from '@chakra-ui/react';

import { Plus, Trash, CaretDown } from '@phosphor-icons/react';

import { RequiredFieldLabel } from '@/components/forms/RequiredFieldLabel';
import { Field } from '@/components/ui/field';
import { useCreateInterviewAgent } from '@/store/createInterviewAgent';

import { QuestionBankDialog } from './QuestionBankDialog';

const ERROR_MESSAGES = {
  MIN_QUESTIONS: 'Please add at least 3 questions to proceed.',
  MAX_QUESTIONS: 'Maximum 15 questions allowed.',
  EMPTY_QUESTION: 'Please fill in all question fields.',
  DUPLICATE_QUESTION:
    'Duplicate questions are not allowed. Please ensure all questions are unique.',
  SUBMIT_ERROR: 'Failed to process questions. Please try again.'
} as const;

const MIN_QUESTIONS = 3;
const MAX_QUESTIONS = 15;
const DESCRIPTION_MAX_LENGTH = 1000;

interface FormValues {
  title: string;
  description: string;
  retakeLimit: number;
  sessionTimeLimit: number;
  questions: { text: string }[];
}

const ErrorMessage = ({ message }: { message?: string }) =>
  message ? (
    <Box fontSize="14px" color="error.700" role="alert">
      {message}
    </Box>
  ) : null;

export function QuestionEditorSection() {
  const [isError, setIsError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Get store data
  const {
    job,
    candidate,
    interviewTitle,
    interviewDescription,
    retakeLimit,
    sessionTimeLimit,
    questions,
    prevStep,
    nextStep,
    setInterviewTitle,
    setInterviewDescription,
    setRetakeLimit,
    setSessionTimeLimit,
    setQuestions
  } = useCreateInterviewAgent();

  // Form setup
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    defaultValues: {
      description: interviewDescription,
      retakeLimit: retakeLimit,
      sessionTimeLimit: sessionTimeLimit,
      questions: questions.length > 0 ? questions.map((q) => ({ text: q })) : [{ text: '' }]
    },
    mode: 'onChange'
  });

  useEffect(() => {
    let computedTitle = '';
    if (interviewTitle && interviewTitle.trim() !== '') {
      computedTitle = interviewTitle;
    } else if (job?.job_title && job.job_title.trim() !== '') {
      computedTitle = `${job.job_title} Interview`;
    } else if (candidate?.first_name && candidate.first_name.trim() !== '') {
      computedTitle = `Interview with ${candidate.first_name}`;
    } else {
      computedTitle = 'Interview';
    }
    setValue('title', computedTitle);
  }, [interviewTitle, job?.job_title, candidate?.first_name, setValue]);

  // Watchers
  const watchRetakeLimit = watch('retakeLimit');
  const watchDescription = watch('description');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'questions'
  });

  // Memoized values
  const questionCount = useMemo(() => fields.length, [fields.length]);
  const canAddQuestion = useMemo(() => questionCount < MAX_QUESTIONS, [questionCount]);
  const canRemoveQuestion = useMemo(() => questionCount > 1, [questionCount]);
  const hasQuestionErrors = useMemo(() => !!errors.questions, [errors.questions]);
  const hasMinMaxErrors = useMemo(
    () =>
      isError === ERROR_MESSAGES.MAX_QUESTIONS ||
      isError === ERROR_MESSAGES.MIN_QUESTIONS ||
      isError === ERROR_MESSAGES.DUPLICATE_QUESTION,
    [isError]
  );

  // Validation helpers
  const validateQuestions = useCallback((questions: { text: string }[]) => {
    if (questions.length < MIN_QUESTIONS) {
      return ERROR_MESSAGES.MIN_QUESTIONS;
    }

    if (questions.length > MAX_QUESTIONS) {
      return ERROR_MESSAGES.MAX_QUESTIONS;
    }

    const emptyQuestions = questions.filter((q) => !q.text.trim());
    if (emptyQuestions.length > 0) {
      return ERROR_MESSAGES.EMPTY_QUESTION;
    }

    const questionTexts = questions.map((q) => q.text.trim().toLowerCase());
    const uniqueQuestionTexts = new Set(questionTexts);
    if (questionTexts.length !== uniqueQuestionTexts.size) {
      return ERROR_MESSAGES.DUPLICATE_QUESTION;
    }

    return null;
  }, []);

  // Handlers
  const handleFormSubmit = useCallback(
    async (formData: FormValues) => {
      try {
        const validationError = validateQuestions(formData.questions);
        if (validationError) {
          setIsError(validationError);
          return;
        }

        // Clear any previous errors
        setIsError(null);

        // Save form data to store
        setInterviewTitle(formData.title);
        setInterviewDescription(formData.description);
        setRetakeLimit(formData.retakeLimit);
        setSessionTimeLimit(formData.sessionTimeLimit);
        setQuestions(formData.questions.map((q) => q.text));

        nextStep();
      } catch (error) {
        console.error('Error processing questions:', error);
        setIsError(ERROR_MESSAGES.SUBMIT_ERROR);
      }
    },
    [
      validateQuestions,
      setInterviewTitle,
      setInterviewDescription,
      setRetakeLimit,
      setSessionTimeLimit,
      setQuestions,
      nextStep
    ]
  );

  const handleBack = useCallback(() => {
    prevStep();
  }, [prevStep]);

  const handleAddQuestion = useCallback(() => {
    if (canAddQuestion) {
      append({ text: '' });
    }
  }, [canAddQuestion, append]);

  const handleRemoveQuestion = useCallback(
    (index: number) => {
      if (canRemoveQuestion) {
        remove(index);
      }
    },
    [canRemoveQuestion, remove]
  );

  const handleSelectUpload = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const handleSelectQuestionBank = useCallback(
    (questionBank: QuestionBank) => {
      if (questionBank.description !== '' && watch('description').trim() === '') {
        setValue('description', questionBank.description);
      }

      const nonEmptyExistingQuestions = fields.filter((field) => field.text.trim() !== '');
      const existingQuestionTexts = new Set(
        nonEmptyExistingQuestions.map((field) => field.text.trim().toLowerCase())
      );

      const newQuestions = questionBank.questions
        .map((q) => ({ text: q.question }))
        .filter((q) => !existingQuestionTexts.has(q.text.trim().toLowerCase()));

      setValue('questions', [...nonEmptyExistingQuestions, ...newQuestions]);
    },
    [fields, setValue, watch]
  );

  return (
    <Box
      w="100%"
      border="1px solid"
      borderColor="neutrals.200"
      borderRadius="12px"
      p="32px"
      bg="background.secondary"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack gap="24px">
          {/* Header */}
          <Stack gap="0" mb="16px">
            <Text fontSize="20px" color="neutrals.900" fontWeight="bold">
              Question Editor
            </Text>
            <Text fontSize="16px" color="neutrals.500">
              Edit your interview question set here.
            </Text>
          </Stack>

          <Stack gap="24px">
            {/* Interview Title */}
            <Flex gap="24px">
              <Field
                flex="2"
                label={
                  <RequiredFieldLabel fontWeight="semibold">Interview Title</RequiredFieldLabel>
                }
                invalid={!!errors.title}
                errorText={<ErrorMessage message={errors.title?.message} />}
              >
                <Input
                  placeholder="E.g. Junior Software Engineer Interview"
                  size="lg"
                  borderRadius="8px"
                  {...register('title', {
                    required: 'Interview title is required',
                    minLength: {
                      value: 1,
                      message: 'Interview title cannot be empty'
                    }
                  })}
                />
              </Field>

              <Field
                flex="1"
                label={
                  <RequiredFieldLabel fontWeight="semibold">
                    Retake Limit Per Question
                  </RequiredFieldLabel>
                }
              >
                <Menu.Root>
                  <Menu.Trigger asChild>
                    <Button
                      variant="outline"
                      size="lg"
                      borderRadius="8px"
                      w="100%"
                      justifyContent="space-between"
                    >
                      <Flex alignItems="center" justify="space-between" flex="1">
                        <Text>{watchRetakeLimit == -1 ? 'unlimited' : watchRetakeLimit}</Text>
                        <CaretDown size={16} />
                      </Flex>
                    </Button>
                  </Menu.Trigger>
                  <Menu.Positioner>
                    <Menu.Content>
                      <Menu.Item
                        value="1"
                        fontSize="16px"
                        cursor="pointer"
                        onClick={() => setValue('retakeLimit', 1)}
                      >
                        1
                      </Menu.Item>
                      <Menu.Item
                        value="3"
                        fontSize="16px"
                        cursor="pointer"
                        onClick={() => setValue('retakeLimit', 3)}
                      >
                        3
                      </Menu.Item>
                      <Menu.Item
                        value="5"
                        fontSize="16px"
                        cursor="pointer"
                        onClick={() => setValue('retakeLimit', 5)}
                      >
                        5
                      </Menu.Item>
                      <Menu.Item
                        value="unlimited"
                        fontSize="16px"
                        cursor="pointer"
                        onClick={() => setValue('retakeLimit', -1)}
                      >
                        Unlimited
                      </Menu.Item>
                    </Menu.Content>
                  </Menu.Positioner>
                </Menu.Root>
              </Field>

              <Field
                flex="1"
                label={
                  <RequiredFieldLabel fontWeight="semibold">Session Time Limit</RequiredFieldLabel>
                }
                invalid={!!errors.sessionTimeLimit}
                errorText={<ErrorMessage message={errors.sessionTimeLimit?.message} />}
              >
                <Flex alignItems="center" gap="8px">
                  <Input
                    type="number"
                    w="100%"
                    min={10}
                    max={120}
                    size="lg"
                    borderRadius="8px"
                    defaultValue={sessionTimeLimit}
                    {...register('sessionTimeLimit', {
                      required: 'Session time limit is required',
                      valueAsNumber: true,
                      min: {
                        value: 10,
                        message: 'Session time limit must be at least 10 minutes'
                      },
                      max: {
                        value: 120,
                        message: 'Session time limit cannot exceed 120 minutes'
                      }
                    })}
                  />
                  <Text fontSize="16px" color="neutrals.600">
                    mins
                  </Text>
                </Flex>
              </Field>
            </Flex>

            {/* Interview Description */}
            <Field
              label={
                <Text fontSize="16px" fontWeight="semibold">
                  Interview Description
                </Text>
              }
              invalid={!!errors.description}
              errorText={<ErrorMessage message={errors.description?.message} />}
            >
              <Box position="relative" w="100%">
                <Textarea
                  placeholder="Add additional instructions for the candidate as necessary."
                  fontSize="16px"
                  minH="120px"
                  resize="vertical"
                  borderRadius="8px"
                  {...register('description', {
                    maxLength: {
                      value: DESCRIPTION_MAX_LENGTH,
                      message: `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters`
                    }
                  })}
                />
                <Text
                  position="absolute"
                  bottom="8px"
                  right="12px"
                  fontSize="12px"
                  color={errors.description ? 'error.700' : 'neutrals.500'}
                  bg="background.secondary"
                  px="4px"
                >
                  {(watchDescription || '').length}/{DESCRIPTION_MAX_LENGTH}
                </Text>
              </Box>
            </Field>

            {/* Interview Questions */}
            <Box>
              <Flex justifyContent="space-between" alignItems="center" mb="16px">
                <Stack gap="0" mb="16px">
                  <Text fontSize="18px" fontWeight="semibold">
                    Interview Questions
                  </Text>
                  <Text fontSize="14px" color="neutrals.500">
                    Please add a minimum of 3 and up to 15 interview questions.
                  </Text>
                </Stack>
                <Button
                  p="12px 16px"
                  fontSize="16px"
                  borderRadius="8px"
                  bg="primary.900"
                  color="neutrals.0"
                  loading={isSubmitting}
                  _hover={{
                    bg: 'primary.700'
                  }}
                  onClick={handleSelectUpload}
                >
                  Import From Question Bank
                </Button>
              </Flex>

              <Box
                border="1px solid"
                borderColor={hasQuestionErrors || hasMinMaxErrors ? 'error.500' : 'neutrals.200'}
                borderRadius="12px"
              >
                <Flex justifyContent="flex-end" p="16px 48px">
                  <Button
                    p="12px 16px"
                    fontSize="16px"
                    border="1px solid"
                    borderColor="primary.900"
                    borderRadius="8px"
                    bg="neutrals.0"
                    color="primary.900"
                    disabled={isSubmitting || !canAddQuestion}
                    _hover={{
                      bg: 'neutrals.50'
                    }}
                    onClick={handleAddQuestion}
                  >
                    <Flex gap="8px" alignItems="center">
                      <Plus size={16} />
                      <Text>Add a question</Text>
                    </Flex>
                  </Button>
                </Flex>
                <Separator />
                <Stack gap="0">
                  {fields.map((field, index) => (
                    <>
                      <Flex key={field.id} alignItems="center" gap="12px" p="16px 24px">
                        <Text fontSize="16px" fontWeight="medium" minW="20px">
                          {index + 1}.
                        </Text>
                        <Field
                          flex="1"
                          invalid={!!errors.questions?.[index]?.text}
                          errorText={
                            <ErrorMessage message={errors.questions?.[index]?.text?.message} />
                          }
                        >
                          <Input
                            placeholder="Enter your question here..."
                            size="lg"
                            fontSize="16px"
                            borderRadius="8px"
                            {...register(`questions.${index}.text`, {
                              required: 'Question text is required',
                              minLength: {
                                value: 5,
                                message: 'Question must be at least 5 characters long'
                              }
                            })}
                          />
                        </Field>
                        <Button
                          variant="ghost"
                          size="sm"
                          p="0"
                          color="neutrals.600"
                          borderRadius="full"
                          disabled={!canRemoveQuestion}
                          _hover={{ bg: 'neutrals.100' }}
                          onClick={() => handleRemoveQuestion(index)}
                        >
                          <Trash size={16} />
                        </Button>
                      </Flex>
                      {index < fields.length - 1 && <Separator />}
                    </>
                  ))}
                </Stack>
              </Box>
            </Box>
          </Stack>
        </Stack>

        {/* Error Message */}
        {isError && (
          <Box color="error.700" fontSize="16px" mt="16px" role="alert">
            {isError}
          </Box>
        )}

        {/* Submit Buttons */}
        <Flex justifyContent="flex-end" mt="32px" gap="16px">
          <Button
            p="12px 64px"
            fontSize="16px"
            border="1px solid"
            borderColor="primary.900"
            borderRadius="8px"
            bg="neutrals.0"
            color="primary.900"
            disabled={isSubmitting}
            onClick={handleBack}
            _hover={{
              bg: 'neutrals.50'
            }}
          >
            Back
          </Button>
          <Button
            type="submit"
            p="12px 50px"
            fontSize="16px"
            borderRadius="8px"
            bg="primary.900"
            color="neutrals.0"
            loading={isSubmitting}
            _hover={{
              bg: 'primary.700'
            }}
          >
            Review
          </Button>
        </Flex>
      </form>

      <QuestionBankDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSelectQuestionBank={handleSelectQuestionBank}
      />
    </Box>
  );
}
